import React, { useCallback, useMemo, memo, Component } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { BarChart3Icon, ImageIcon, CheckCircleIcon, TrendingUpIcon, PlusCircleIcon, LayersIcon, SendIcon, PlayCircleIcon, ClockIcon, UsersIcon, AlertTriangleIcon, CalendarIcon, FileTextIcon, BuildingIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MOCK_ACTIVITY, LETGROW_COHORTS } from '../schemas/mockData';
import { safeMap } from '../utils/safeArray';
// Memoized stat card component
const StatCard = memo(({
  icon: Icon,
  value,
  label,
  badge,
  colorClass
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  value: string | number;
  label: string;
  badge?: string;
  colorClass: string;
}) => <div className="stat-card hover:border-blue-300" data-id="element-790">
      <div className="flex items-center justify-between mb-4" data-id="element-791">
        <div className={`w-11 h-11 bg-gradient-to-br ${colorClass} rounded-xl flex items-center justify-center shadow-sm`} data-id="element-792">
          <Icon className="w-6 h-6 text-white" data-id="element-793" />
        </div>
        {badge && <span className="badge badge-blue" data-id="element-794">{badge}</span>}
      </div>
      <div className="text-3xl font-bold text-gray-900 mb-1" data-id="element-795">{value}</div>
      <div className="text-sm font-medium text-gray-600" data-id="element-796">{label}</div>
    </div>);
StatCard.displayName = 'StatCard';
// Memoized action card component
const ActionCard = memo(({
  icon: Icon,
  title,
  description,
  onClick,
  colorClass
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  onClick: () => void;
  colorClass: string;
}) => <button onClick={onClick} className="p-5 border-2 border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all text-left group bg-white" data-id="element-797">
      <div className="flex items-start gap-4" data-id="element-798">
        <div className={`w-12 h-12 bg-gradient-to-br ${colorClass} rounded-xl flex items-center justify-center group-hover:from-${colorClass.split('-')[1]}-200 group-hover:to-${colorClass.split('-')[1]}-300 transition-all shadow-sm`} data-id="element-799">
          <Icon className={`w-6 h-6 text-${colorClass.split('-')[1]}-600`} data-id="element-800" />
        </div>
        <div className="flex-1" data-id="element-801">
          <h3 className="font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors" data-id="element-802">
            {title}
          </h3>
          <p className="text-sm text-gray-600" data-id="element-803">{description}</p>
        </div>
      </div>
    </button>);
ActionCard.displayName = 'ActionCard';
// Get organization stats - memoized
const getOrgStats = (orgId: string) => {
  const letgrowCohortsLength = Array.isArray(LETGROW_COHORTS) ? LETGROW_COHORTS.length : 0;
  const statsMap: {
    [key: string]: any;
  } = {
    'org-kmg': {
      activeExercises: 12,
      imagesInLibrary: 2847,
      completions30d: 4823,
      completionRate: 87.3,
      totalParticipants: 12456,
      activeCohorts: 247,
      atRiskCount: 1847,
      activeAssignments: 156,
      schoolCount: 143
    },
    'org-duquesne': {
      activeExercises: 6,
      imagesInLibrary: 1240,
      completions30d: 428,
      completionRate: 89.5,
      totalParticipants: 1250,
      activeCohorts: 8,
      atRiskCount: 45,
      activeAssignments: 6,
      schoolCount: 4
    },
    'org-letgrow': {
      activeExercises: 11,
      imagesInLibrary: 1240,
      completions30d: 2486,
      completionRate: 89.7,
      totalParticipants: 900,
      activeCohorts: letgrowCohortsLength,
      atRiskCount: 72,
      activeAssignments: 11,
      schoolCount: 4,
      efficacyMetrics: {
        participantGrowth: 23.5,
        resilienceIncrease: 18.2,
        confidenceGain: 21.8,
        parentSatisfaction: 94.3
      }
    }
  };
  return statsMap[orgId] || {
    activeExercises: 8,
    imagesInLibrary: 1240,
    completions30d: 342,
    completionRate: 91.2,
    totalParticipants: 456,
    activeCohorts: 12,
    atRiskCount: 34,
    activeAssignments: 8,
    schoolCount: 5
  };
};
export const Dashboard = memo(() => {
  const {
    t,
    currentOrganization
  } = useOrganization();
  const {
    role
  } = useAuth();
  const navigate = useNavigate();
  const isClientAdmin = role === 'client-admin';
  const isLetGrow = currentOrganization.id === 'org-letgrow';
  // Memoize stats calculation
  const MOCK_STATS = useMemo(() => getOrgStats(currentOrganization.id), [currentOrganization.id]);
  // Memoize activity items
  const activityItems = useMemo(() => Array.isArray(MOCK_ACTIVITY) ? MOCK_ACTIVITY : [], []);
  // Memoize navigation callbacks
  const handleNavigate = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);
  return <div className="page-container" data-id="element-804">
      <div className="page-main" data-id="element-805">
        <div className="page-main-content" data-id="element-806">
          {/* Header */}
          <div className="mb-8 animate-slide-in-down" data-id="element-807">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-808">
              {t.dashboard}
            </h1>
            <p className="text-sm text-gray-600" data-id="element-809">{t.dashboardSubtitle}</p>
          </div>
          {/* Stats Grid - Enhanced spacing and alignment */}
          <div className="stats-grid mb-8" data-id="element-810">
            <StatCard icon={BarChart3Icon} value={MOCK_STATS.activeExercises} label={t.activeExercises} badge={t.active || 'Active'} colorClass="from-blue-500 to-blue-600" data-id="element-811" />
            <StatCard icon={UsersIcon} value={MOCK_STATS.totalParticipants.toLocaleString()} label={t.totalParticipants} badge={`${MOCK_STATS.activeCohorts} ${t.cohorts || 'Cohorts'}`} colorClass="from-green-500 to-green-600" data-id="element-812" />
            <StatCard icon={CheckCircleIcon} value={MOCK_STATS.completions30d.toLocaleString()} label={t.completions30d} badge={`${MOCK_STATS.completionRate}%`} colorClass="from-purple-500 to-purple-600" data-id="element-813" />
            <StatCard icon={AlertTriangleIcon} value={MOCK_STATS.atRiskCount.toLocaleString()} label={t.atRiskParticipants} badge={t.flagged || 'Flagged'} colorClass="from-red-500 to-red-600" data-id="element-814" />
          </div>
          {/* LetGrow Efficacy Metrics */}
          {isLetGrow && MOCK_STATS.efficacyMetrics && <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-8 shadow-sm animate-scale-in" data-id="element-815">
              <div className="mb-6" data-id="element-816">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3" data-id="element-817">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-sm" data-id="element-818">
                    <TrendingUpIcon className="w-5 h-5 text-white" data-id="element-819" />
                  </div>
                  {t.programImpactEfficacy || 'Program Impact & Efficacy'}
                </h2>
                <p className="text-sm text-gray-600 ml-[52px]" data-id="element-820">
                  {t.programImpactDesc || 'Measurable outcomes demonstrating the effectiveness of the LetGrow independence-building program'}
                </p>
              </div>
              <div className="stats-grid" data-id="element-821">
                <StatCard icon={TrendingUpIcon} value={`+${MOCK_STATS.efficacyMetrics.participantGrowth}%`} label={t.independenceGrowth || 'Independence Growth'} colorClass="from-blue-500 to-blue-600" data-id="element-822" />
                <StatCard icon={CheckCircleIcon} value={`+${MOCK_STATS.efficacyMetrics.resilienceIncrease}%`} label={t.resilienceIncrease || 'Resilience Increase'} colorClass="from-green-500 to-green-600" data-id="element-823" />
                <StatCard icon={UsersIcon} value={`+${MOCK_STATS.efficacyMetrics.confidenceGain}%`} label={t.confidenceGain || 'Confidence Gain'} colorClass="from-purple-500 to-purple-600" data-id="element-824" />
                <StatCard icon={UsersIcon} value={`${MOCK_STATS.efficacyMetrics.parentSatisfaction}%`} label={t.parentSatisfaction || 'Parent Satisfaction'} colorClass="from-orange-500 to-orange-600" data-id="element-825" />
              </div>
            </div>}
          {/* Quick Actions & Recent Activity - Enhanced grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-826">
            {/* Quick Actions */}
            <div className="lg:col-span-2" data-id="element-827">
              <div className="content-card" data-id="element-828">
                <div className="p-6" data-id="element-829">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3" data-id="element-830">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm" data-id="element-831">
                      <PlusCircleIcon className="w-5 h-5 text-white" data-id="element-832" />
                    </div>
                    {t.quickActions}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-833">
                    {isClientAdmin ? <>
                        <ActionCard icon={UsersIcon} title={t.manageParticipants} description={t.manageParticipantsDesc} onClick={() => handleNavigate('/participant-management')} colorClass="from-green-100 to-green-200" data-id="element-834" />
                        <ActionCard icon={SendIcon} title={t.createAssignment} description={t.createAssignmentDesc} onClick={() => handleNavigate('/assignment-management')} colorClass="from-blue-100 to-blue-200" data-id="element-835" />
                      </> : <>
                        <ActionCard icon={FileTextIcon} title={t.createNewExercise} description={t.createNewExerciseDesc} onClick={() => handleNavigate('/exercise-authoring')} colorClass="from-blue-100 to-blue-200" data-id="element-836" />
                        <ActionCard icon={LayersIcon} title={t.buildImageSet} description={t.buildImageSetDesc} onClick={() => handleNavigate('/image-sets')} colorClass="from-purple-100 to-purple-200" data-id="element-837" />
                      </>}
                    <ActionCard icon={BarChart3Icon} title={t.viewResults} description={t.viewResultsDesc} onClick={() => handleNavigate('/results')} colorClass="from-indigo-100 to-indigo-200" data-id="element-838" />
                    <ActionCard icon={PlayCircleIcon} title={t.previewRunner} description={t.previewRunnerDesc} onClick={() => handleNavigate('/runner-preview')} colorClass="from-orange-100 to-orange-200" data-id="element-839" />
                  </div>
                </div>
              </div>
            </div>
            {/* Recent Activity */}
            <div className="content-card" data-id="element-840">
              <div className="p-6" data-id="element-841">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3" data-id="element-842">
                  <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl flex items-center justify-center shadow-sm" data-id="element-843">
                    <ClockIcon className="w-5 h-5 text-white" data-id="element-844" />
                  </div>
                  {t.recentActivity}
                </h2>
                <div className="space-y-3" data-id="element-845">
                  {safeMap('Dashboard.activityItems', activityItems, activity => {
                  const Icon = activity.icon;
                  const colorClasses = {
                    blue: 'bg-blue-100 text-blue-600 border-blue-200',
                    green: 'bg-green-100 text-green-600 border-green-200',
                    purple: 'bg-purple-100 text-purple-600 border-purple-200',
                    red: 'bg-red-100 text-red-600 border-red-200',
                    indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200'
                  }[activity.color];
                  return <div key={activity.id} className="flex items-start gap-3 p-4 rounded-xl hover:bg-gray-50 transition-all border border-transparent hover:border-gray-200 cursor-pointer" data-id="element-846">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${colorClasses} shadow-sm`} data-id="element-847">
                            <Icon className="w-5 h-5" data-id="element-848" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-849">
                            <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-850">
                              {activity.title}
                            </p>
                            <p className="text-xs text-gray-500 font-medium" data-id="element-851">
                              {activity.time}
                            </p>
                          </div>
                        </div>;
                })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
});
Dashboard.displayName = 'Dashboard';